import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';

// Define the data structure for export
export interface ExportData {
  [key: string]: any;
}

// Define column structure for export
export interface ExportColumn {
  key: string;
  label: string;
  width?: number;
}

// PDF Export Function
export const exportToPDF = (
  data: ExportData[],
  columns: ExportColumn[],
  title: string = 'Report',
  filename: string = 'report.pdf'
) => {
  try {
    // Create new PDF document
    const doc = new jsPDF('l', 'mm', 'a4'); // Landscape orientation
    
    // Add title
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text(title, 14, 22);
    
    // Add date and time
    const now = new Date();
    const dateTime = now.toLocaleString();
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Generated on: ${dateTime}`, 14, 30);
    
    // Add total count
    doc.text(`Total Records: ${data.length}`, 14, 36);
    
    // Prepare data for table
    const tableData = data.map((row, index) => {
      const rowData: any[] = [index + 1]; // Add row number
      columns.forEach(col => {
        let value = row[col.key] || '-';
        
        // Format specific fields
        if (col.key === 'packdt' || col.key === 'receivedt') {
          value = value ? new Date(value).toLocaleString() : '-';
        } else if (typeof value === 'string' && value.length > 30) {
          value = value.substring(0, 30) + '...';
        }
        
        rowData.push(value);
      });
      return rowData;
    });
    
    // Prepare headers
    const headers = ['S.No.', ...columns.map(col => col.label)];
    
    // Add table
    autoTable(doc, {
      head: [headers],
      body: tableData,
      startY: 45,
      styles: {
        fontSize: 8,
        cellPadding: 2,
        overflow: 'linebreak',
        halign: 'left'
      },
      headStyles: {
        fillColor: [41, 128, 185], // Blue header
        textColor: 255,
        fontStyle: 'bold'
      },
      alternateRowStyles: {
        fillColor: [245, 245, 245] // Light gray alternate rows
      },
      columnStyles: {
        0: { halign: 'center', cellWidth: 15 }, // S.No. column
      },
      margin: { left: 14, right: 14 },
      tableWidth: 'auto',
      showHead: 'everyPage'
    });
    
    // Save the PDF
    doc.save(filename);
    
    return true;
  } catch (error) {
    console.error('Error exporting to PDF:', error);
    throw new Error('Failed to export PDF');
  }
};

// Excel Export Function
export const exportToExcel = (
  data: ExportData[],
  columns: ExportColumn[],
  title: string = 'Report',
  filename: string = 'report.xlsx'
) => {
  try {
    // Prepare data for Excel
    const excelData = data.map((row, index) => {
      const rowData: any = { 'S.No.': index + 1 };
      
      columns.forEach(col => {
        let value = row[col.key] || '-';
        
        // Format specific fields
        if (col.key === 'packdt' || col.key === 'receivedt') {
          value = value ? new Date(value).toLocaleString() : '-';
        }
        
        rowData[col.label] = value;
      });
      
      return rowData;
    });
    
    // Create workbook and worksheet
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(excelData);
    
    // Set column widths
    const columnWidths = [
      { wch: 8 }, // S.No.
      ...columns.map(col => ({ wch: col.width || 15 }))
    ];
    worksheet['!cols'] = columnWidths;
    
    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Report');
    
    // Add metadata
    if (!workbook.Props) {
      workbook.Props = {};
    }
    workbook.Props.Title = title;
    workbook.Props.Subject = 'Sabarimala Vanchi Report';
    workbook.Props.Author = 'Sabarimala System';
    workbook.Props.CreatedDate = new Date();
    
    // Save the Excel file
    XLSX.writeFile(workbook, filename);
    
    return true;
  } catch (error) {
    console.error('Error exporting to Excel:', error);
    throw new Error('Failed to export Excel');
  }
};

// Utility function to generate filename with timestamp
export const generateFilename = (prefix: string, extension: string): string => {
  const now = new Date();
  const timestamp = now.toISOString().slice(0, 19).replace(/:/g, '-');
  return `${prefix}_${timestamp}.${extension}`;
};

// Utility function to format data for export (remove sensitive data, format dates, etc.)
export const formatDataForExport = (data: ExportData[]): ExportData[] => {
  return data.map(row => {
    const formattedRow = { ...row };
    
    // Format dates
    if (formattedRow.packdt) {
      formattedRow.packdt = new Date(formattedRow.packdt).toLocaleString();
    }
    if (formattedRow.receivedt) {
      formattedRow.receivedt = new Date(formattedRow.receivedt).toLocaleString();
    }
    
    // Clean up any sensitive or unnecessary fields
    delete formattedRow.tblid; // Remove internal IDs
    delete formattedRow.id;
    
    return formattedRow;
  });
};
